"use client";

import { useEffect, useState } from "react";

import { DxIcon } from "../../../dx/ui/icons";
import { ARCHITECTURE_FILTER_OPTIONS } from "../../../reservation-filters/architecture-filter-options";
import { FilterChip } from "../../ui/filter-chip";
import { TriggerField } from "../../ui/trigger-field";

import { useTriggerToggle } from "./_hooks/use-trigger-toggle";
import { ConditionFilter, type ConditionDraft } from "./condition-filter";
import { FACILITY_FILTERS } from "./facility-filter-options";
import { FilterPopupShell } from "./filter-popup-shell";

export type ApplyConditions = {
  architectureTypes: string[];
  facilities: string[];
  largeOnly: boolean;
  evCharger: boolean;
};

/**
 * 絞り込み条件フィルター: サイドバーセクション（ラベル＋トリガー＋適用チップ＋ポップアップ）。
 *
 * Figma（5179-11423 絞り込みpopup / 5179-11914 中身）準拠:
 * - ポップアップ内: 設備セクション ＋ 建築タイプセクション（2列）＋ 区切り線 ＋ Largeのみ
 *
 * 状態モデル:
 * - ポップアップ内操作は draft をトグル（即時適用しない）。
 * - 開く度に draft を確定値から再初期化。
 * - 決定: 4次元を一括適用（onApplyConditions）→ close。
 * - クリア（ポップアップ内）: draft を全解除 → 未適用（決定で反映）。
 * - チップ×: 即時適用（per-item toggle）。
 * - セクションクリア: 全次元を空/false に即時適用。
 */
export function ConditionFilterPopup({
  selectedArchTypes,
  onToggleArchType,
  selectedFacilities,
  onToggleFacility,
  largeOnly,
  onToggleLargeOnly,
  evChargerAvailable,
  onToggleEvCharger,
  onApplyConditions,
  externalOpen,
  onExternalClose,
  variant = "filter-sidebar",
  externalAnchorEl,
}: {
  selectedArchTypes: ReadonlySet<string>;
  onToggleArchType: (key: string) => void;
  selectedFacilities: ReadonlySet<string>;
  onToggleFacility: (slug: string) => void;
  largeOnly: boolean;
  onToggleLargeOnly: () => void;
  evChargerAvailable: boolean;
  onToggleEvCharger: () => void;
  /** 決定・セクションクリア時の4次元一括適用 */
  onApplyConditions: (conditions: ApplyConditions) => void;
  /** SP 検索バーなど外部からポップアップを開く場合に true を渡す */
  externalOpen?: boolean;
  /** externalOpen で開いたポップアップが閉じられたときのコールバック */
  onExternalClose?: () => void;
  /** モバイルでも Drawer でなく Popover（全幅・bottom-start）として表示する */
  variant?: "filter-sidebar" | "filter-search";
  /**
   * 外部からアンカー要素を渡す場合に使用（SP 検索バーなど）。
   * 指定時は内部 anchorEl より優先される。
   */
  externalAnchorEl?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<ConditionDraft>({
    architectureTypes: new Set(),
    facilities: new Set(),
    largeOnly: false,
    evCharger: false,
  });

  const hasSelection =
    selectedArchTypes.size > 0 ||
    selectedFacilities.size > 0 ||
    largeOnly ||
    evChargerAvailable;

  const triggerLabel = hasSelection ? "条件選択あり" : "絞り込み条件";

  const handleOpen = () => {
    // 開く度に確定値でドラフトを再初期化
    setDraft({
      architectureTypes: new Set(selectedArchTypes),
      facilities: new Set(selectedFacilities),
      largeOnly,
      evCharger: evChargerAvailable,
    });
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // × / 外側クリック / Esc → ドラフト破棄
      setOpen(false);
      onExternalClose?.();
    }
  };

  // トリガー（外部 anchor）を開閉トグルにする（outside-press との競合を回避）
  const triggerToggle = useTriggerToggle(open, handleOpen, () =>
    setOpen(false),
  );

  // externalOpen が true になったら Drawer を開く（SP 検索バーからのトリガー）
  useEffect(() => {
    if (externalOpen) {
      handleOpen();
    } else if (externalOpen === false) {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const toggleDraftArchType = (key: string) => {
    setDraft((prev) => {
      const next = new Set(prev.architectureTypes);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, architectureTypes: next };
    });
  };

  const toggleDraftFacility = (slug: string) => {
    setDraft((prev) => {
      const next = new Set(prev.facilities);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return { ...prev, facilities: next };
    });
  };

  const toggleDraftLargeOnly = () => {
    setDraft((prev) => ({ ...prev, largeOnly: !prev.largeOnly }));
  };

  const toggleDraftEvCharger = () => {
    setDraft((prev) => ({ ...prev, evCharger: !prev.evCharger }));
  };

  const handleClear = () => {
    setDraft({
      architectureTypes: new Set(),
      facilities: new Set(),
      largeOnly: false,
      evCharger: false,
    });
  };

  const canClear =
    draft.architectureTypes.size > 0 ||
    draft.facilities.size > 0 ||
    draft.largeOnly ||
    draft.evCharger;

  const handleConfirm = () => {
    onApplyConditions({
      architectureTypes: [...draft.architectureTypes],
      facilities: [...draft.facilities],
      largeOnly: draft.largeOnly,
      evCharger: draft.evCharger,
    });
    setOpen(false);
  };

  const handleSectionClear = () => {
    onApplyConditions({
      architectureTypes: [],
      facilities: [],
      largeOnly: false,
      evCharger: false,
    });
  };

  return (
    <div className="flex flex-col gap-[8px]">
      <p className="font-notojp text-dx-text-default text-[14px] leading-[24px] font-bold">
        絞り込み条件
      </p>

      {/* ref を callback ref で取得（AreaFilterPopup と同じ作法） */}
      <div
        ref={(el) => setAnchorEl(el)}
        onPointerDown={triggerToggle.onPointerDown}
      >
        <TriggerField
          icon={<DxIcon src="/i/dx/tune.svg" />}
          label={triggerLabel}
          hasValue={hasSelection}
          onClick={triggerToggle.onClick}
          data-analytics-id="room_type_browse_opened_condition_filter"
        />
      </div>

      {hasSelection && (
        <div className="flex items-center gap-[8px] px-[16px] py-[8px]">
          <div className="flex flex-wrap gap-[8px]">
            {[...selectedArchTypes].map((value) => (
              <FilterChip
                key={value}
                // ARCHITECTURE_FILTER_OPTIONS に無い拠点固有の値（例: arch=CURRENT）も
                // 選択済みならチップとして表示できるよう、実データ起点＋ラベルfallbackにする
                label={
                  ARCHITECTURE_FILTER_OPTIONS.find((o) => o.value === value)
                    ?.label ?? value
                }
                onRemove={() => onToggleArchType(value)}
              />
            ))}
            {FACILITY_FILTERS.filter((f) => selectedFacilities.has(f.slug)).map(
              (facility) => (
                <FilterChip
                  key={facility.slug}
                  label={facility.label}
                  onRemove={() => onToggleFacility(facility.slug)}
                />
              ),
            )}
            {largeOnly && (
              <FilterChip label="Largeのみ" onRemove={onToggleLargeOnly} />
            )}
            {evChargerAvailable && (
              <FilterChip label="EV充電器" onRemove={onToggleEvCharger} />
            )}
          </div>
          <button
            data-analytics-id="room_type_browse_clicked_condition_clear"
            type="button"
            onClick={handleSectionClear}
            className="font-notojp text-dx-text-default focus-visible:ring-dx-border-focus shrink-0 rounded-sm text-[12px] leading-none underline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            クリア
          </button>
        </div>
      )}

      {/* 絞り込み条件ポップアップ */}
      <FilterPopupShell
        open={open}
        onOpenChange={handleOpenChange}
        anchorEl={externalAnchorEl ?? anchorEl}
        title="絞り込み条件を選択"
        canClear={canClear}
        canConfirm={true}
        onClear={handleClear}
        onConfirm={handleConfirm}
        variant={variant}
      >
        <ConditionFilter
          draft={draft}
          onToggleArchType={toggleDraftArchType}
          onToggleFacility={toggleDraftFacility}
          onToggleLargeOnly={toggleDraftLargeOnly}
          onToggleEvCharger={toggleDraftEvCharger}
        />
      </FilterPopupShell>
    </div>
  );
}
